// ============================================================
// UserDashboard.jsx
//
// Personal dashboard for registered users who are pending
// course access approval from the admin.
//
// Tabs:
//   1. Welcome      — Motivation, platform purpose
//   2. Profile      — Complete application form + LinkedIn
//   3. My Aims      — User's learning aims
//   4. My Goals     — User's goals table
//   5. Courses      — Locked/blurred until admin grants access
//   6. How To Use   — Platform instructions
// ============================================================

import React, { useState, useEffect } from "react";
import {
    Box, Grid, Typography, TextField, Button, Avatar,
    Chip, CircularProgress, Snackbar, Alert, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, IconButton, Tooltip, styled,
} from "@mui/material";

import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SchoolIcon from "@mui/icons-material/School";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CodeIcon from "@mui/icons-material/Code";

import { auth, db } from "../../firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

// ============================================================
//  CONSTANTS
// ============================================================

const PLANS = ["Basic", "Standard", "Premium Individual", "Enterprise"];

const NATIONALITIES = [
    "Nigerian", "Ghanaian", "Kenyan", "South African", "British",
    "American", "Canadian", "Australian", "Indian", "Other"
];

/**
 * COURSES
 * Real course catalogue shown in the Courses tab.
 * Blurred and locked until admin grants courseAccess.
 */
const COURSES = [
    { title: "Java Developer",       color: "#f44336", topics: ["Java Basics", "OOP", "Collections", "Multithreading", "Spring", "Advanced APIs"] },
    { title: "Python Developer",     color: "#1976d2", topics: ["Python Basics", "Data Structures", "OOP", "Django/Flask", "Data Science", "Advanced Patterns"] },
    { title: "C++ Developer",        color: "#9c27b0", topics: ["C++ Basics", "Pointers", "OOP", "STL", "Templates", "Advanced Features"] },
    { title: "C# Developer",         color: "#4caf50", topics: ["C# Basics", "OOP", ".NET", "LINQ", "Entity Framework", "Advanced Patterns"] },
    { title: "Typescript Developer", color: "#ff9800", topics: ["TS Basics", "Interfaces", "Generics", "Decorators", "Angular/React", "Advanced TS"] },
    { title: "Spring Boot",          color: "#009688", topics: ["Spring Boot Basics", "REST APIs", "JPA", "Security", "Microservices", "Advanced Boot"] },
    { title: "Express.js",           color: "#795548", topics: ["Node Basics", "Routing", "Middleware", "REST APIs", "Auth", "Advanced Express"] },
    { title: "MongoDB",              color: "#43a047", topics: ["NoSQL Basics", "CRUD", "Aggregation", "Indexing", "Replication", "Sharding"] },
    { title: "MySQL",                color: "#2196f3", topics: ["SQL Basics", "Joins", "Procedures", "Indexes", "Optimization", "Advanced SQL"] },
    { title: "Postgres",             color: "#673ab7", topics: ["Schemas", "Functions", "Indexes", "Extensions", "Optimization", "Advanced Features"] },
    { title: "Javascript",           color: "#fbc02d", topics: ["JS Basics", "DOM", "ES6+", "Async/Await", "Frameworks", "Advanced Patterns"] },
    { title: "Github",               color: "#212121", topics: ["Git Basics", "Branching", "Pull Requests", "CI/CD", "Actions", "Advanced Workflows"] },
    { title: "Linux",                color: "#607d8b", topics: ["Shell Basics", "File System", "Networking", "Security", "Processes", "Advanced Admin"] },
    { title: "Embedded IoT",         color: "#e91e63", topics: ["IoT Basics", "Microcontrollers", "Firmware", "Protocols", "RTOS", "Advanced IoT"] },
    { title: "Software Testing",     color: "#ff5722", topics: ["Unit Testing", "Integration", "Automation", "Performance", "QA Tools", "Advanced Testing"] },
    { title: "HTML5 & CSS",          color: "#e34f26", topics: ["HTML Basics", "CSS Fundamentals", "Flexbox", "Grid", "Responsive Design", "Advanced CSS"] },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

const DashboardWrapper = styled(Box)(() => ({
    minHeight: "100vh",
    backgroundColor: "#f4f5f9",
    fontFamily: "'Georgia', serif",
}));

const Sidebar = styled(Box)(({ theme }) => ({
    width: 260,
    minHeight: "100vh",
    backgroundColor: "#09122C",
    padding: "32px 0",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 100,
    [theme.breakpoints.down("md")]: {
        width: "100%",
        minHeight: "auto",
        position: "relative",
        flexDirection: "row",
        padding: "12px 16px",
        overflowX: "auto",
    },
}));

const MainContent = styled(Box)(({ theme }) => ({
    marginLeft: 260,
    padding: "40px 36px",
    [theme.breakpoints.down("md")]: {
        marginLeft: 0,
        padding: "24px 16px",
    },
}));

const NavItem = styled(Box)(({ active }) => ({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 28px",
    cursor: "pointer",
    color: active ? "goldenrod" : "rgba(255,255,255,0.6)",
    backgroundColor: active ? "rgba(218,165,32,0.1)" : "transparent",
    borderLeft: active ? "3px solid goldenrod" : "3px solid transparent",
    transition: "all 0.2s ease",
    fontSize: "0.9rem",
    fontWeight: active ? 700 : 400,
    whiteSpace: "nowrap",
    "&:hover": {
        color: "goldenrod",
        backgroundColor: "rgba(218,165,32,0.08)",
    },
}));

const SectionCard = styled(Paper)(() => ({
    padding: "32px",
    borderRadius: "16px",
    boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.05)",
    marginBottom: "24px",
}));

const GoldButton = styled(Button)(() => ({
    backgroundColor: "#09122C",
    color: "goldenrod",
    fontWeight: "bold",
    "&:hover": { backgroundColor: "#142850" },
    "&:disabled": { backgroundColor: "#ccc", color: "#888" },
}));

// ============================================================
//  TAB CONFIG
// ============================================================

const TABS = [
    { id: "welcome",    label: "Welcome",    icon: <FavoriteIcon fontSize="small" /> },
    { id: "profile",    label: "My Profile", icon: <EditIcon fontSize="small" /> },
    { id: "aims",       label: "My Aims",    icon: <EmojiObjectsIcon fontSize="small" /> },
    { id: "goals",      label: "My Goals",   icon: <TrackChangesIcon fontSize="small" /> },
    { id: "courses",    label: "Courses",    icon: <SchoolIcon fontSize="small" /> },
    { id: "how-to-use", label: "How To Use", icon: <HelpOutlineIcon fontSize="small" /> },
];

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function UserDashboard() {
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("welcome");
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const [form, setForm] = useState({
        firstname: "", lastname: "", username: "",
        phone: "", nationality: "", plan: "",
        linkedin: "", email: "",
    });

    const [aims, setAims] = useState([]);
    const [goals, setGoals] = useState([]);
    const [newAim, setNewAim] = useState("");
    const [newGoal, setNewGoal] = useState({ title: "", deadline: "", status: "In Progress" });

    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
    const notify = (message, severity = "success") =>
        setSnack({ open: true, message, severity });

    // ============================================================
    //  LOAD USER PROFILE
    // ============================================================

    useEffect(() => {
        const loadProfile = async () => {
            const user = auth.currentUser;
            if (!user) { navigate("/signin"); return; }

            try {
                const snap = await getDoc(doc(db, "users", user.uid));
                if (snap.exists()) {
                    const data = snap.data();
                    setProfile(data);
                    setForm({
                        firstname:   data.firstname   || "",
                        lastname:    data.lastname    || "",
                        username:    data.username    || "",
                        phone:       data.phone       || "",
                        nationality: data.nationality || "",
                        plan:        data.plan        || "",
                        linkedin:    data.linkedin    || "",
                        email:       data.email       || user.email,
                    });
                    setAims(data.aims   || []);
                    setGoals(data.goals || []);
                }
            } catch (err) {
                console.error("Error loading profile:", err);
                notify("Failed to load profile.", "error");
            } finally {
                setLoading(false);
            }
        };

        loadProfile();
    }, [navigate]);

    // ============================================================
    //  PROFILE HANDLERS
    // ============================================================

    const handleSaveProfile = async () => {
        const user = auth.currentUser;
        if (!user) return;
        try {
            setSaving(true);
            await updateDoc(doc(db, "users", user.uid), {
                firstname:       form.firstname,
                lastname:        form.lastname,
                username:        form.username,
                phone:           form.phone,
                nationality:     form.nationality,
                plan:            form.plan,
                linkedin:        form.linkedin,
                profileComplete: true,
            });
            setProfile((prev) => ({ ...prev, ...form, profileComplete: true }));
            setEditing(false);
            notify("Profile updated successfully!");
        } catch (err) {
            notify("Failed to save profile.", "error");
        } finally {
            setSaving(false);
        }
    };

    // ============================================================
    //  AIMS HANDLERS
    // ============================================================

    const handleAddAim = async () => {
        if (!newAim.trim()) return;
        const updated = [...aims, { id: Date.now(), text: newAim.trim() }];
        setAims(updated);
        setNewAim("");
        await updateDoc(doc(db, "users", auth.currentUser.uid), { aims: updated });
    };

    const handleDeleteAim = async (id) => {
        const updated = aims.filter((a) => a.id !== id);
        setAims(updated);
        await updateDoc(doc(db, "users", auth.currentUser.uid), { aims: updated });
    };

    // ============================================================
    //  GOALS HANDLERS
    // ============================================================

    const handleAddGoal = async () => {
        if (!newGoal.title.trim()) return;
        const updated = [...goals, { id: Date.now(), ...newGoal }];
        setGoals(updated);
        setNewGoal({ title: "", deadline: "", status: "In Progress" });
        await updateDoc(doc(db, "users", auth.currentUser.uid), { goals: updated });
    };

    const handleDeleteGoal = async (id) => {
        const updated = goals.filter((g) => g.id !== id);
        setGoals(updated);
        await updateDoc(doc(db, "users", auth.currentUser.uid), { goals: updated });
    };

    // ============================================================
    //  SIGN OUT
    // ============================================================

    const handleSignOut = async () => {
        await signOut(auth);
        navigate("/home");
    };

    // ============================================================
    //  LOADING STATE
    // ============================================================

    if (loading) {
        return (
            <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: "100vh" }}>
                <CircularProgress sx={{ color: "goldenrod" }} />
            </Grid>
        );
    }

    // ============================================================
    //  TAB RENDERERS
    // ============================================================

    const renderWelcome = () => (
        <Box>
            <SectionCard>
                <Box sx={{
                    background: "linear-gradient(135deg, #09122C 0%, #1a2a5e 100%)",
                    borderRadius: "12px", padding: "40px", color: "white", mb: 3,
                    position: "relative", overflow: "hidden",
                }}>
                    <Box sx={{
                        position: "absolute", right: -40, top: -40,
                        width: 200, height: 200, borderRadius: "50%",
                        backgroundColor: "rgba(218,165,32,0.1)",
                    }} />
                    <Typography variant="h4" fontWeight={800} gutterBottom
                                sx={{ fontFamily: "'Georgia', serif", color: "goldenrod" }}>
                        Welcome, {profile?.firstname || profile?.username || "there"} 👋
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 600, lineHeight: 1.8 }}>
                        You've taken the first step toward transforming your career in software development.
                        This platform is designed to equip you with industry-standard skills across
                        16 professional development tracks.
                    </Typography>
                    <Box mt={3}>
                        {profile?.courseAccess ? (
                            <Chip icon={<CheckCircleIcon />} label="Course Access Granted ✓"
                                  sx={{ backgroundColor: "goldenrod", color: "#09122C", fontWeight: "bold" }} />
                        ) : (
                            <Chip icon={<LockIcon />} label="Awaiting Course Access — Payment Pending"
                                  sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "white" }} />
                        )}
                    </Box>
                </Box>

                {!profile?.profileComplete && (
                    <Box sx={{
                        p: 2.5, borderRadius: "10px",
                        backgroundColor: "rgba(218,165,32,0.08)",
                        border: "1px solid rgba(218,165,32,0.3)",
                        display: "flex", alignItems: "center",
                        justifyContent: "space-between", flexWrap: "wrap", gap: 2,
                    }}>
                        <Box>
                            <Typography fontWeight={700} color="#09122C">Complete your profile</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Help us know you better by filling in your profile details.
                            </Typography>
                        </Box>
                        <GoldButton variant="contained" onClick={() => setActiveTab("profile")}>
                            Complete Profile
                        </GoldButton>
                    </Box>
                )}
            </SectionCard>

            <SectionCard>
                <Typography variant="h6" fontWeight={700} color="#09122C" gutterBottom
                            sx={{ fontFamily: "'Georgia', serif" }}>
                    What You'll Learn
                </Typography>
                <Grid container spacing={3} mt={1}>
                    {[
                        { icon: "💻", title: "16 Course Tracks", desc: "From Java to IoT — comprehensive coverage of modern software development." },
                        { icon: "🚀", title: "Industry Ready", desc: "Learn tools and frameworks used by professional developers worldwide." },
                        { icon: "🎯", title: "Structured Path", desc: "Each track takes you from basics to advanced with clear milestones." },
                    ].map((item) => (
                        <Grid item xs={12} md={4} key={item.title}>
                            <Box sx={{
                                p: 3, borderRadius: "12px",
                                backgroundColor: "#f9fafc",
                                border: "1px solid rgba(0,0,0,0.06)", height: "100%",
                            }}>
                                <Typography fontSize="2rem" mb={1}>{item.icon}</Typography>
                                <Typography fontWeight={700} color="#09122C" gutterBottom>{item.title}</Typography>
                                <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </SectionCard>
        </Box>
    );

    const renderProfile = () => (
        <SectionCard>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={700} color="#09122C"
                            sx={{ fontFamily: "'Georgia', serif" }}>
                    My Profile
                </Typography>
                {!editing ? (
                    <GoldButton startIcon={<EditIcon />} variant="contained" onClick={() => setEditing(true)}>
                        Edit Profile
                    </GoldButton>
                ) : (
                    <Box display="flex" gap={1}>
                        <Button startIcon={<CancelIcon />} variant="outlined"
                                onClick={() => setEditing(false)}
                                sx={{ borderColor: "#09122C", color: "#09122C" }}>
                            Cancel
                        </Button>
                        <GoldButton
                            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            variant="contained" onClick={handleSaveProfile} disabled={saving}>
                            Save
                        </GoldButton>
                    </Box>
                )}
            </Box>

            <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="First Name" value={form.firstname}
                               onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                               disabled={!editing} variant={editing ? "outlined" : "filled"} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Last Name" value={form.lastname}
                               onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                               disabled={!editing} variant={editing ? "outlined" : "filled"} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Username" value={form.username}
                               onChange={(e) => setForm({ ...form, username: e.target.value })}
                               disabled={!editing} variant={editing ? "outlined" : "filled"} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Email Address" value={form.email}
                               disabled variant="filled" helperText="Email cannot be changed" />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Phone Number" value={form.phone}
                               onChange={(e) => setForm({ ...form, phone: e.target.value })}
                               disabled={!editing} variant={editing ? "outlined" : "filled"} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField select fullWidth label="Nationality" value={form.nationality}
                               onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                               disabled={!editing} variant={editing ? "outlined" : "filled"}>
                        {NATIONALITIES.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField select fullWidth label="Plan" value={form.plan}
                               onChange={(e) => setForm({ ...form, plan: e.target.value })}
                               disabled={!editing} variant={editing ? "outlined" : "filled"}>
                        {PLANS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="LinkedIn Profile URL" value={form.linkedin}
                               onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                               disabled={!editing} variant={editing ? "outlined" : "filled"}
                               placeholder="https://linkedin.com/in/yourprofile"
                               InputProps={{ startAdornment: <LinkedInIcon sx={{ mr: 1, color: "#0077b5" }} /> }} />
                </Grid>
            </Grid>

            <Box mt={3}>
                <Chip
                    icon={profile?.profileComplete ? <CheckCircleIcon /> : <EditIcon />}
                    label={profile?.profileComplete ? "Profile Complete" : "Profile Incomplete"}
                    color={profile?.profileComplete ? "success" : "warning"}
                    variant="outlined"
                />
            </Box>
        </SectionCard>
    );

    const renderAims = () => (
        <SectionCard>
            <Typography variant="h6" fontWeight={700} color="#09122C" gutterBottom
                        sx={{ fontFamily: "'Georgia', serif", mb: 3 }}>
                My Learning Aims
            </Typography>
            <Box display="flex" gap={2} mb={3}>
                <TextField fullWidth label="Add a new aim" value={newAim}
                           onChange={(e) => setNewAim(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && handleAddAim()}
                           placeholder="e.g. Master Java Spring Boot by end of year" />
                <GoldButton variant="contained" startIcon={<AddIcon />} onClick={handleAddAim}>
                    Add
                </GoldButton>
            </Box>
            {aims.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>
                    No aims added yet. Start by adding what you want to achieve.
                </Typography>
            ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                    {aims.map((aim, index) => (
                        <Box key={aim.id} display="flex" alignItems="center"
                             justifyContent="space-between"
                             sx={{ p: 2, borderRadius: "10px", backgroundColor: "#f9fafc", border: "1px solid rgba(0,0,0,0.06)" }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography sx={{
                                    width: 28, height: 28, borderRadius: "50%",
                                    backgroundColor: "goldenrod", color: "#09122C",
                                    display: "flex", alignItems: "center",
                                    justifyContent: "center", fontWeight: 700,
                                    fontSize: "0.8rem", flexShrink: 0,
                                }}>
                                    {index + 1}
                                </Typography>
                                <Typography>{aim.text}</Typography>
                            </Box>
                            <Tooltip title="Delete aim">
                                <IconButton size="small" onClick={() => handleDeleteAim(aim.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            )}
        </SectionCard>
    );

    const renderGoals = () => (
        <SectionCard>
            <Typography variant="h6" fontWeight={700} color="#09122C" gutterBottom
                        sx={{ fontFamily: "'Georgia', serif", mb: 3 }}>
                My Goals
            </Typography>
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={5}>
                    <TextField fullWidth label="Goal Title" value={newGoal.title}
                               onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                               placeholder="e.g. Complete Java Developer track" />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField fullWidth label="Deadline" type="date"
                               value={newGoal.deadline}
                               onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                               InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField select fullWidth label="Status" value={newGoal.status}
                               onChange={(e) => setNewGoal({ ...newGoal, status: e.target.value })}>
                        {["Not Started", "In Progress", "Completed"].map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={12} md={2}>
                    <GoldButton fullWidth variant="contained" startIcon={<AddIcon />}
                                onClick={handleAddGoal} sx={{ height: "56px" }}>
                        Add
                    </GoldButton>
                </Grid>
            </Grid>
            {goals.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={4}>
                    No goals added yet. Define what you want to accomplish.
                </Typography>
            ) : (
                <TableContainer component={Paper} elevation={0}
                                sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: "10px" }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#09122C" }}>
                            <TableRow>
                                {["Goal", "Deadline", "Status", ""].map((h) => (
                                    <TableCell key={h} sx={{ color: "goldenrod", fontWeight: 700 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {goals.map((goal) => (
                                <TableRow key={goal.id} hover>
                                    <TableCell>{goal.title}</TableCell>
                                    <TableCell>{goal.deadline || "—"}</TableCell>
                                    <TableCell>
                                        <Chip label={goal.status} size="small"
                                              color={goal.status === "Completed" ? "success" : goal.status === "In Progress" ? "warning" : "default"} />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Delete goal">
                                            <IconButton size="small" onClick={() => handleDeleteGoal(goal.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </SectionCard>
    );

    /**
     * renderCourses
     * Shows all 16 course cards with their topics.
     * Blurred and locked with overlay when courseAccess is false.
     * Fully visible and interactive when courseAccess is true.
     */
    const renderCourses = () => (
        <Box>
            {/* Payment pending banner */}
            {!profile?.courseAccess && (
                <Box sx={{
                    p: 3, mb: 3, borderRadius: "12px",
                    backgroundColor: "#09122C", color: "white",
                    display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                }}>
                    <LockIcon sx={{ color: "goldenrod", fontSize: 32 }} />
                    <Box>
                        <Typography fontWeight={700} fontSize="1rem" color="goldenrod">
                            Course Access Pending
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Your payment is being confirmed. Once verified, your administrator
                            will grant you full access to all 16 course tracks.
                        </Typography>
                    </Box>
                </Box>
            )}

            <Grid container spacing={3}>
                {COURSES.map((course) => (
                    <Grid item xs={12} sm={6} lg={4} key={course.title}>
                        <Box sx={{ position: "relative" }}>
                            {/* Course Card */}
                            <Box sx={{
                                p: 3, borderRadius: "14px",
                                border: "1px solid rgba(0,0,0,0.08)",
                                backgroundColor: "white",
                                filter: profile?.courseAccess ? "none" : "blur(3px)",
                                pointerEvents: profile?.courseAccess ? "auto" : "none",
                                userSelect: "none",
                                transition: "all 0.3s ease",
                                "&:hover": profile?.courseAccess ? {
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                    transform: "translateY(-2px)",
                                } : {},
                            }}>
                                {/* Colored top bar */}
                                <Box sx={{
                                    height: 5, borderRadius: "4px",
                                    backgroundColor: course.color, mb: 2,
                                }} />

                                {/* Icon + Title */}
                                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                                    <Box sx={{
                                        width: 36, height: 36, borderRadius: "8px",
                                        backgroundColor: `${course.color}18`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                        <CodeIcon sx={{ color: course.color, fontSize: 20 }} />
                                    </Box>
                                    <Typography fontWeight={700} color="#09122C" fontSize="0.95rem">
                                        {course.title}
                                    </Typography>
                                </Box>

                                {/* Topics */}
                                <Box display="flex" flexWrap="wrap" gap={0.7}>
                                    {course.topics.map((topic) => (
                                        <Chip key={topic} label={topic} size="small"
                                              sx={{
                                                  fontSize: "0.7rem",
                                                  backgroundColor: `${course.color}12`,
                                                  color: course.color,
                                                  fontWeight: 600,
                                                  border: `1px solid ${course.color}30`,
                                              }} />
                                    ))}
                                </Box>
                            </Box>

                            {/* Lock overlay per card when access is pending */}
                            {!profile?.courseAccess && (
                                <Box sx={{
                                    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    borderRadius: "14px",
                                    backgroundColor: "rgba(255,255,255,0.3)",
                                }}>
                                    <LockIcon sx={{ color: "#09122C", fontSize: 28, opacity: 0.5 }} />
                                </Box>
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const renderHowToUse = () => (
        <SectionCard>
            <Typography variant="h6" fontWeight={700} color="#09122C" gutterBottom
                        sx={{ fontFamily: "'Georgia', serif", mb: 3 }}>
                How To Use This Platform
            </Typography>
            <Box display="flex" flexDirection="column" gap={3}>
                {[
                    { step: "01", title: "Complete Your Profile", desc: "Head to the 'My Profile' tab and fill in all your details including your LinkedIn URL. This helps your administrator process your application faster." },
                    { step: "02", title: "Set Your Aims & Goals", desc: "Use the 'My Aims' and 'My Goals' tabs to define what you want to achieve. This keeps you focused and motivated throughout your learning journey." },
                    { step: "03", title: "Confirm Your Payment", desc: "Course access is granted after payment confirmation. Contact your administrator to complete the payment process for your selected plan." },
                    { step: "04", title: "Get Course Access", desc: "Once payment is confirmed, your administrator will grant you access to the Course Hub where all your learning materials are available." },
                    { step: "05", title: "Start Learning", desc: "With course access granted, navigate to the Courses tab and begin your development journey across 16 professional course tracks." },
                ].map((item) => (
                    <Box key={item.step} display="flex" gap={3} alignItems="flex-start"
                         sx={{ p: 3, borderRadius: "12px", backgroundColor: "#f9fafc", border: "1px solid rgba(0,0,0,0.05)" }}>
                        <Typography sx={{
                            fontSize: "1.8rem", fontWeight: 900, color: "goldenrod",
                            fontFamily: "'Georgia', serif", lineHeight: 1, flexShrink: 0,
                        }}>
                            {item.step}
                        </Typography>
                        <Box>
                            <Typography fontWeight={700} color="#09122C" gutterBottom>{item.title}</Typography>
                            <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{item.desc}</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </SectionCard>
    );

    // ============================================================
    //  TAB RENDER MAP
    // ============================================================

    const renderTab = () => {
        switch (activeTab) {
            case "welcome":    return renderWelcome();
            case "profile":    return renderProfile();
            case "aims":       return renderAims();
            case "goals":      return renderGoals();
            case "courses":    return renderCourses();
            case "how-to-use": return renderHowToUse();
            default:           return renderWelcome();
        }
    };

    // ============================================================
    //  MAIN RENDER
    // ============================================================

    return (
        <DashboardWrapper>
            <Box display="flex" flexDirection={{ xs: "column", md: "row" }}>

                {/* ── SIDEBAR ── */}
                <Sidebar>
                    <Box px={3} mb={4} display={{ xs: "none", md: "block" }}>
                        <Typography variant="h6" fontWeight={900}
                                    sx={{ color: "goldenrod", fontFamily: "'Georgia', serif", letterSpacing: "1px" }}>
                            MY DASHBOARD
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
                            {profile?.plan || "Plan Pending"}
                        </Typography>
                    </Box>

                    <Box px={3} mb={3} display={{ xs: "none", md: "flex" }} alignItems="center" gap={2}>
                        <Avatar sx={{
                            backgroundColor: "goldenrod", color: "#09122C",
                            fontWeight: 800, width: 44, height: 44,
                        }}>
                            {(profile?.firstname?.[0] || profile?.username?.[0] || "U").toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight={700} color="white">
                                {profile?.firstname
                                    ? `${profile.firstname} ${profile.lastname}`
                                    : profile?.username || "User"
                                }
                            </Typography>
                            <Typography variant="caption" color="rgba(255,255,255,0.4)">
                                {profile?.email}
                            </Typography>
                        </Box>
                    </Box>

                    <Box flex={1}>
                        {TABS.map((tab) => (
                            <NavItem key={tab.id} active={activeTab === tab.id ? 1 : 0}
                                     onClick={() => setActiveTab(tab.id)}>
                                {tab.icon}
                                <span>{tab.label}</span>
                            </NavItem>
                        ))}
                    </Box>

                    <Box px={3} mt={4} display={{ xs: "none", md: "block" }}>
                        <Button fullWidth variant="outlined" onClick={handleSignOut}
                                sx={{
                                    borderColor: "rgba(255,255,255,0.2)",
                                    color: "rgba(255,255,255,0.6)",
                                    "&:hover": { borderColor: "goldenrod", color: "goldenrod" },
                                }}>
                            Sign Out
                        </Button>
                    </Box>
                </Sidebar>

                {/* ── MAIN CONTENT ── */}
                <MainContent flex={1}>
                    <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h5" fontWeight={800} color="#09122C"
                                        sx={{ fontFamily: "'Georgia', serif" }}>
                                {TABS.find((t) => t.id === activeTab)?.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {new Date().toLocaleDateString("en-GB", {
                                    weekday: "long", year: "numeric",
                                    month: "long", day: "numeric",
                                })}
                            </Typography>
                        </Box>
                        <Chip
                            icon={profile?.courseAccess ? <CheckCircleIcon /> : <LockIcon />}
                            label={profile?.courseAccess ? "Access Granted" : "Pending Access"}
                            color={profile?.courseAccess ? "success" : "warning"}
                            variant="outlined"
                            sx={{ display: { xs: "none", sm: "flex" } }}
                        />
                    </Box>

                    {renderTab()}
                </MainContent>
            </Box>

            <Snackbar open={snack.open} autoHideDuration={4000}
                      onClose={() => setSnack({ ...snack, open: false })}>
                <Alert severity={snack.severity}
                       onClose={() => setSnack({ ...snack, open: false })}
                       sx={{ width: "100%" }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </DashboardWrapper>
    );
}
